import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Building2, ChevronDown, Check } from 'lucide-react';

const TenantSelector = () => {
  const { user, selectTenant } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Get current tenant info
  const currentTenant = user?.selectedTenant;
  // Filter to only show tenants that the user actually has access to
  // This is a defensive measure in case the backend returns all tenants
  const allTenants = user?.tenants || [];
  const availableTenants = allTenants.filter(tenant => {
    // Only include tenants that have an accessLevel (indicating user assignment)
    // Regular users should only see tenants they're assigned to
    // If a tenant doesn't have accessLevel, it means it's not assigned to the user
    const hasAccessLevel = !!(tenant.accessLevel || tenant.access_level);
    const hasAssignmentId = !!(tenant.assignment_id || tenant.tenant_id);
    
    // Include tenant if it has accessLevel OR if it's the current selected tenant
    // (to handle edge cases where current tenant might not have accessLevel set)
    const isCurrentTenant = (tenant.code || tenant.tenant_code) === (currentTenant?.code || currentTenant?.tenant_code);
    
    return hasAccessLevel || (hasAssignmentId && isCurrentTenant);
  });
  const hasMultipleTenants = availableTenants.length > 1;
  
  // Debug logging
  useEffect(() => {
    if (allTenants.length > 0) {
      console.log('🔍 TENANT_SELECTOR: Tenant filtering', {
        userId: user?.userId,
        userType: user?.userType,
        allTenantsCount: allTenants.length,
        filteredTenantsCount: availableTenants.length,
        allTenants: allTenants.map(t => ({
          code: t.code || t.tenant_code,
          name: t.name || t.tenant_name,
          hasAccessLevel: !!(t.accessLevel || t.access_level),
          accessLevel: t.accessLevel || t.access_level,
          isDefault: t.isDefault,
          hasAssignmentId: !!(t.assignment_id || t.tenant_id)
        })),
        filteredTenants: availableTenants.map(t => ({
          code: t.code || t.tenant_code,
          name: t.name || t.tenant_name,
          accessLevel: t.accessLevel || t.access_level
        }))
      });
      
      // Warn if filtering removed tenants (indicates backend might be returning all tenants)
      if (allTenants.length > availableTenants.length) {
        console.warn('⚠️ TENANT_SELECTOR: Filtered out tenants without accessLevel. Backend might be returning all tenants instead of user-assigned tenants.', {
          removedCount: allTenants.length - availableTenants.length,
          removedTenants: allTenants
            .filter(t => !availableTenants.includes(t))
            .map(t => ({ code: t.code || t.tenant_code, name: t.name || t.tenant_name }))
        });
      }
    }
  }, [allTenants, availableTenants, user?.userId, user?.userType, currentTenant]);

  // Helper function to get tenant name (handles both database and normalized formats)
  const getTenantName = (tenant) => {
    return tenant?.name || tenant?.tenant_name || tenant?.code || tenant?.tenant_code;
  };

  // Helper function to get tenant code (handles both database and normalized formats)
  const getTenantCode = (tenant) => {
    return tenant?.code || tenant?.tenant_code;
  };

  // Helper function to get access level (handles both database and normalized formats)
  const getAccessLevel = (tenant) => {
    return tenant?.accessLevel || tenant?.access_level;
  };

  // Debug logging to see tenant state
  useEffect(() => {
    if (availableTenants.length > 0) {
      const displayTenant = currentTenant || availableTenants[0];
      console.log('TenantSelector state:', {
        currentTenant: getTenantName(currentTenant),
        displayTenant: getTenantName(displayTenant),
        availableTenants: availableTenants.map(t => `${getTenantName(t)} (${getTenantCode(t)})`),
        tenantCount: availableTenants.length,
        hasMultipleTenants: hasMultipleTenants,
        rawTenantData: availableTenants
      });
    }
  }, [currentTenant, availableTenants, hasMultipleTenants]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle tenant selection
  const handleTenantSelect = async (tenant) => {
    const tenantCode = getTenantCode(tenant);
    const currentTenantCode = getTenantCode(currentTenant);
    
    console.log('🔍 TENANT_SELECTOR: Starting tenant selection', {
      selectedTenant: getTenantName(tenant),
      tenantCode,
      currentTenantCode,
      isLoading
    });
    
    if (tenantCode === currentTenantCode || isLoading) {
      console.log('🔍 TENANT_SELECTOR: Skipping selection - same tenant or loading');
      setIsOpen(false);
      return;
    }

    let safetyTimer;
    try {
      setIsLoading(true);
      console.log('🔍 TENANT_SELECTOR: Calling selectTenant with code:', tenantCode);

      // Safety: reset loading if backend hangs
      safetyTimer = setTimeout(() => {
        console.warn('⏱️ TENANT_SELECTOR: Tenant switch timeout - resetting loading state');
        setIsLoading(false);
      }, 15000);
      
      // Switch tenant in the backend
      const result = await selectTenant(tenantCode);
      console.log('🔍 TENANT_SELECTOR: selectTenant result:', result);
      
      if (!result?.success) {
        setIsLoading(false);
        return;
      }
      
      // Close dropdown
      setIsOpen(false);
      
      // Redirect to customers list to ensure consistent state after tenant switch
      const redirectPath = '/customers';
      console.log(`🔍 TENANT_SELECTOR: Tenant switched to: ${getTenantName(tenant)} (${getTenantCode(tenant)}). Redirecting to ${redirectPath}...`);
      window.location.assign(redirectPath);
      
    } catch (error) {
      console.error('🔍 TENANT_SELECTOR: Error switching tenant:', error);
      setIsLoading(false);
      // You could add a toast notification here for error feedback
    } finally {
      if (safetyTimer) clearTimeout(safetyTimer);
    }
    // Note: On success, setIsLoading(false) is not needed because page will reload
  };

  // Don't render if no tenants available
  if (availableTenants.length === 0) {
    return null;
  }

  // If no current tenant but we have tenants, show the first one as default
  const displayTenant = currentTenant || availableTenants[0];

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Tenant Display/Button - Responsive: compact on mobile, full on desktop */}
      <button
        onClick={() => hasMultipleTenants && setIsOpen(!isOpen)}
        disabled={isLoading || !hasMultipleTenants}
        className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 rounded-md transition-colors duration-200 ${
          hasMultipleTenants 
            ? 'hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer' 
            : 'cursor-default'
        } ${isLoading ? 'opacity-50' : ''}`}
        title={hasMultipleTenants ? 'Click to switch tenant' : 'Current tenant'}
      >
        <Building2 className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
        <span className="max-w-[80px] sm:max-w-32 truncate">
          {getTenantName(displayTenant)}
        </span>
        {hasMultipleTenants && (
          <ChevronDown 
            className={`h-3 w-3 sm:h-4 sm:w-4 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''} ${
              isLoading ? 'animate-spin' : ''
            }`} 
          />
        )}
        {isLoading && (
          <span className="text-[10px] sm:text-xs text-blue-600 dark:text-blue-400 ml-1 sm:ml-2 hidden sm:inline">
            Switching...
          </span>
        )}
      </button>

      {/* Dropdown Menu - Responsive positioning */}
      {hasMultipleTenants && isOpen && (
        <div className="absolute right-0 mt-2 w-56 sm:w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg z-50 max-h-[70vh] overflow-y-auto">
          <div className="py-1">
            <div className="px-3 sm:px-4 py-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-200 dark:border-gray-700">
              Switch Tenant
            </div>
            {availableTenants.map((tenant) => {
              const isSelected = getTenantCode(tenant) === getTenantCode(displayTenant);
              return (
                <button
                  key={tenant.assignment_id || tenant.tenant_id || tenant.id}
                  onClick={() => handleTenantSelect(tenant)}
                  disabled={isLoading}
                  className={`w-full flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                    isSelected 
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' 
                      : 'text-gray-700 dark:text-gray-300'
                  } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                    <Building2 className={`h-4 w-4 flex-shrink-0 ${
                      isSelected 
                        ? 'text-blue-600 dark:text-blue-400' 
                        : 'text-gray-400 dark:text-gray-500'
                    }`} />
                    <div className="text-left min-w-0">
                      <div className="font-medium truncate">{getTenantName(tenant)}</div>
                      <div className="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 truncate">
                        {getTenantCode(tenant)} • {getAccessLevel(tenant)}
                      </div>
                    </div>
                  </div>
                  {isSelected && (
                    <Check className="h-4 w-4 text-blue-600 dark:text-blue-400 flex-shrink-0 ml-2" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantSelector;
